import type { ApiResponse } from "~/types/ApiResponse";
import { ApiBaseService } from "./ApiBaseService";
import type { FetchRequest, FetchOptions } from "ofetch";

export interface AiToolkitJsonSchemaProperty {
    type: "string" | "number" | "boolean" | "object" | "array" | "null";
    description: string;
    enum?: string[];
    items?: AiToolkitJsonSchemaProperty;
    properties?: {
        [key: string]: AiToolkitJsonSchemaProperty;
    };
    required?: string[];
    additionalProperties?: boolean;
}

export interface AiToolkitJsonSchema {
    name: string;
    description: string;
    schema: {
        type: "object";
        properties: {
            [key: string]: AiToolkitJsonSchemaProperty;
        };
        required: string[];
        additionalProperties: false;
    };
    strict?: boolean;
}
/**
 * Utility type that converts a JSON schema to a TypeScript type
 */
export type JsonSchemaToType<T extends AiToolkitJsonSchema> = {
    [K in keyof T["schema"]["properties"]]: JsonSchemaPropertyToType<
        T["schema"]["properties"][K]
    >;
};

/**
 * Helper type to convert a JSON schema property to its corresponding TypeScript type
 */
type JsonSchemaPropertyToType<T extends AiToolkitJsonSchemaProperty> =
    T["type"] extends "string"
        ? string
        : T["type"] extends "number"
          ? number
          : T["type"] extends "boolean"
            ? boolean
            : T["type"] extends "null"
              ? null
              : T["type"] extends "array"
                ? T["items"] extends AiToolkitJsonSchemaProperty
                    ? JsonSchemaPropertyToType<T["items"]>[]
                    : any[]
                : T["type"] extends "object"
                  ? T["properties"] extends object
                      ? {
                            [K in keyof T["properties"]]: T["properties"][K] extends AiToolkitJsonSchemaProperty
                                ? JsonSchemaPropertyToType<T["properties"][K]>
                                : any;
                        }
                      : Record<string, any>
                  : any;

export interface GenerateTextCompletionsParams {
    input: string;
    maxTokens?: number;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface SuggestImprovementsParams {
    input: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface AnalyzeInputParams {
    input: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface SummarizeContentParams {
    content: string;
    maxTokens?: number;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface SuggestKeywordsParams {
    content: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface TranslateTextParams {
    text: string;
    targetLanguage: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface ValidateAndCleanInputParams {
    input: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface DetectIntentParams {
    input: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface GenerateRecommendationsParams {
    preferences: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export interface PerformSentimentAnalysisParams {
    text: string;
    additional_instructions?: string;
    json_schema?: AiToolkitJsonSchema;
}

export class AiToolkitService extends ApiBaseService {
    /**
     * Generate text completions based on the input.
     */
    public generateTextCompletions(
        params: GenerateTextCompletionsParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/generate-text";
        const options: FetchOptions = {
            method: "POST",
            body: {
                input: params.input,
                max_tokens: params.maxTokens ?? 150,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Provide suggestions for improving content or documents.
     */
    public suggestImprovements(
        params: SuggestImprovementsParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/suggest-improvements";
        const options: FetchOptions = {
            method: "POST",
            body: {
                input: params.input,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Analyze user-provided input and return key insights or data.
     */
    public analyzeInput(
        params: AnalyzeInputParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/analyze-input";
        const options: FetchOptions = {
            method: "POST",
            body: {
                input: params.input,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Summarize the provided content.
     */
    public summarizeContent(
        params: SummarizeContentParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/summarize-content";
        const options: FetchOptions = {
            method: "POST",
            body: {
                content: params.content,
                max_tokens: params.maxTokens ?? 100,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Suggest keywords based on the provided content.
     */
    public suggestKeywords(
        params: SuggestKeywordsParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/suggest-keywords";
        const options: FetchOptions = {
            method: "POST",
            body: {
                content: params.content,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Translate the provided text into a target language.
     */
    public translateText(
        params: TranslateTextParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/translate-text";
        const options: FetchOptions = {
            method: "POST",
            body: {
                text: params.text,
                target_language: params.targetLanguage,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Validate and format the provided user input.
     */
    public validateAndCleanInput(
        params: ValidateAndCleanInputParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/validate-clean-input";
        const options: FetchOptions = {
            method: "POST",
            body: {
                input: params.input,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Detect the intent of the user's input or query.
     */
    public detectIntent(
        params: DetectIntentParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/detect-intent";
        const options: FetchOptions = {
            method: "POST",
            body: {
                input: params.input,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Generate personalized recommendations based on preferences or history.
     */
    public generateRecommendations(
        params: GenerateRecommendationsParams,
    ): Promise<
        ApiResponse<
            (typeof params)["json_schema"] extends AiToolkitJsonSchema
                ? JsonSchemaToType<(typeof params)["json_schema"]>
                  : any
        >
    > {
        const route: FetchRequest = "ai/generate-recommendations";
        const options: FetchOptions = {
            method: "POST",
            body: {
                preferences: params.preferences,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }

    /**
     * Perform sentiment analysis on user-provided text.
     */
    public performSentimentAnalysis<T extends AiToolkitJsonSchema = never>(
        params: PerformSentimentAnalysisParams,
    ): Promise<
        ApiResponse<
        (typeof params)["json_schema"] extends AiToolkitJsonSchema
            ? JsonSchemaToType<(typeof params)["json_schema"]>
              : any
        >
    > {
        const route: FetchRequest = "ai/sentiment-analysis";
        const options: FetchOptions = {
            method: "POST",
            body: {
                text: params.text,
                additional_instructions: params.additional_instructions,
                json_schema: params.json_schema,
            },
        };

        return this.request(route, options);
    }
}
